import { Action, ActionService } from "@/services/ActionService";
import { Code } from "@/services/CodeService";
import { Calendar, CheckCircle, ClipboardPlus, Flag, Pencil, } from "lucide-react";
import { useEffect, useState } from "react";
import { priorityColors, actionStatusColors } from "@/lib/task-utils";

interface ActionListDetails {
  featureId: string;
  codes: Code[];
  featureName: string
}

export interface ActionListItem {
  id: string;
  name: string;
  priority: Code | undefined;
  status: Code | undefined;
  startDate: string | null;
  endDate: string | null;
  attachmentUrl: string | null;
}

export const ActionList: React.FC<ActionListDetails> = ({ featureId, codes, featureName }) => {
  const [actionsList, setActionsList] = useState<ActionListItem[]>([]);
  const [editActionId, setEditActionId] = useState<string>("");

  const mapToActionListItem = (action: Action) : ActionListItem => {
    return ({
      id: action.id,
      endDate: action.endDate,
      name: action.name,
      priority: getCode(action.priorityCodeId),
      startDate: action.startDate,
      status: getCode(action.statusCodeId),
      attachmentUrl: action.attachmentUrl,
    })
  }

  const getCode = (codeId: string): Code | undefined => {
    return codes.find((c) => c.id == codeId);
  };

  useEffect(() => {
    const initializeActions = async () => {
      if (!featureId) {
        return;
      }
      const actions = await ActionService.getByFeatureId(featureId);
      const temp: ActionListItem[] = [];
      actions.forEach((a) => {
        temp.push(mapToActionListItem(a));
      });
      setActionsList(temp);
    };
    initializeActions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureId]);

  return (
    <div className="container mx-auto p-4">
      {!editActionId && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold">{featureName} Action List</h1>
            <button
              className="group focus:outline-none"
              aria-label="Add new task"
            >
              <ClipboardPlus className="h-6 w-6 text-gray-500 group-hover:text-blue-600 transition-colors duration-200 cursor-pointer" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    Name
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    <Flag className="inline-block mr-1 h-4 w-4" /> Priority
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    <CheckCircle className="inline-block mr-1 h-4 w-4" /> Status
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    <Calendar className="inline-block mr-1 h-4 w-4" /> Start
                    Date
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    <Calendar className="inline-block mr-1 h-4 w-4" /> End Date
                  </th>
                  <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700">
                    Edit
                  </th>
                </tr>
              </thead>
              <tbody>
                {actionsList.map((action) => (
                  <tr
                    key={action.id}
                    className="border-t border-gray-200 hover:bg-gray-50"
                  >
                    <td className="py-2 px-4 text-sm text-gray-900">
                      {action.name}
                    </td>
                    <td className="py-2 px-4 text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          priorityColors[action.priority!.id]
                        }`}
                      >
                        {action.priority?.name}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          actionStatusColors[action.status!.id]
                        }`}
                      >
                        {action.status?.name}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-sm text-gray-600">
                      {action.startDate}
                    </td>
                    <td className="py-2 px-4 text-sm text-gray-600">
                      {action.endDate}
                    </td>
                    <td className="py-2 px-4 text-sm text-gray-600">
                      <Pencil
                        onClick={() => setEditActionId(action.id)}
                        className="inline-block mr-1 h-4 w-4 hover:text-blue-600 transition-colors duration-200 cursor-pointer"
                        aria-label={`Edit ${action.name}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};